import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { apply, applyWhen, schema, validate } from '@angular/forms/signals';
import { CustomValidators } from '@shared-ikp/utils/validators';
import { validateOptionalWithControlValidator, validateRequired, validateWithControlValidator } from '@shared-ikp/utils/signal-forms.util';

export enum VertragspartnerMode {
  VERWALTETER_KUNDE = 'VERWALTETER_KUNDE',
  ACCOUNT_KUNDE = 'ACCOUNT_KUNDE',
  BEARBEITBAR = 'BEARBEITBAR',
}
export interface AdresseFormValue {
  plz: string;
  ort: string;
  strasse: string;
  hausnummer: string;
  stiege: string;
  tuernummer: string;
}

export interface RechnungsempfaengerFormValue {
  hasAbweichendenRechnungsempfaenger: boolean;
  name: string;
  kundennummer: string;
  adresse: AdresseFormValue;
  uid: string;
  firmenbuchnummer: string;
}

export type VertragspartnerTyp = 'privat' | 'unternehmen' | null;

export interface PrivatpersonKontaktdatenFormValue {
  vorname: string;
  nachname: string;
  email: string;
  telefon: string;
  geburtsdatum: NgbDateStruct | null;
}

export interface UnternehmenKontaktdatenFormValue {
  name: string;
  geburtsdatum: NgbDateStruct | null;
  uid: string;
  firmenbuchnummer: string;
  email: string;
  telefon: string;
}

export interface PrivatUnternehmenFormValue {
  vertragspartnerTyp: VertragspartnerTyp;
  privatpersonKontaktdaten: PrivatpersonKontaktdatenFormValue | null;
  unternehmenKontaktdaten: UnternehmenKontaktdatenFormValue | null;
  rechnungsempfaenger: RechnungsempfaengerFormValue;
}

export interface UnterschriftFormValue {
  unterschrift: string | null;
  hasZustimmung: boolean;
}

export const adresseSchema = schema<AdresseFormValue>(path => {
  validate(path.plz, validateOptionalWithControlValidator(CustomValidators.plzValidator, 'address'));
  validate(path.ort, validateOptionalWithControlValidator(CustomValidators.cityValidator, 'address'));
  validate(path.strasse, validateOptionalWithControlValidator(CustomValidators.streetValidator, 'address'));
  validate(path.hausnummer, validateOptionalWithControlValidator(CustomValidators.houseNumberStaircaseValidator, 'address'));
  validate(path.stiege, validateOptionalWithControlValidator(CustomValidators.houseNumberStaircaseValidator, 'address'));
  validate(path.tuernummer, validateWithControlValidator(CustomValidators.doorNumberValidator, 'address'));
});

export const privatpersonKontaktdatenSchema = schema<PrivatpersonKontaktdatenFormValue>(path => {
  validate(path.vorname, validateOptionalWithControlValidator(CustomValidators.firstNameValidator, 'contact'));
  validate(path.nachname, validateOptionalWithControlValidator(CustomValidators.lastNameValidator, 'contact'));
  validate(path.email, validateWithControlValidator(CustomValidators.optionalEmailValidator, 'contact'));
  validate(path.telefon, validateWithControlValidator(CustomValidators.optionalPhoneNumberValidator, 'contact'));
});

export const privatpersonGeburtsdatumSchema = schema<PrivatpersonKontaktdatenFormValue>(path => {
  validate(path.geburtsdatum, validateWithControlValidator(CustomValidators.optionalBirthdateValidator, 'contact'));
});

export const unternehmenskennungenSchema = schema<{ uid: string; firmenbuchnummer: string }>(path => {
  validate(path.uid, validateWithControlValidator(CustomValidators.optionalUidValidator, 'contact'));
  validate(path.firmenbuchnummer, validateWithControlValidator(CustomValidators.optionalFirmenbuchNrValidator, 'contact'));
});

export const unternehmenKontaktdatenSchema = schema<UnternehmenKontaktdatenFormValue>(path => {
  validate(path.name, validateOptionalWithControlValidator(CustomValidators.companyNameValidator, 'contact'));
  validate(path.geburtsdatum, validateWithControlValidator(CustomValidators.optionalBirthdateValidator, 'contact'));
  apply(path, unternehmenskennungenSchema);
  validate(path.email, validateWithControlValidator(CustomValidators.optionalEmailValidator, 'contact'));
  validate(path.telefon, validateWithControlValidator(CustomValidators.optionalPhoneNumberValidator, 'contact'));
});

export const privatUnternehmenKontaktdatenSchema = schema<PrivatUnternehmenFormValue>(path => {
  applyWhen(
    path.privatpersonKontaktdaten,
    ctx => ctx.valueOf(path.vertragspartnerTyp) === 'privat' && ctx.value() !== null,
    privatpersonKontaktdatenSchema,
  );
  applyWhen(
    path.unternehmenKontaktdaten,
    ctx => ctx.valueOf(path.vertragspartnerTyp) === 'unternehmen' && ctx.value() !== null,
    unternehmenKontaktdatenSchema,
  );
});

const rechnungsempfaengerAdresseSchema = schema<RechnungsempfaengerFormValue>(path => {
  validate(path.name, validateOptionalWithControlValidator(CustomValidators.companyNameValidator, 'rechnungsempfaenger'));
  apply(path.adresse, adresseSchema);
});

export const abweichenderRechnungsempfaengerSchema = schema<RechnungsempfaengerFormValue>(path => {
  applyWhen(path, ctx => ctx.value().hasAbweichendenRechnungsempfaenger, rechnungsempfaengerAdresseSchema);
});

/*
 * Required-field rules. Kept apart from the format rules above because a draft may leave them
 * unfulfilled — they only apply when a step is validated for completeness, see
 * `WizardStepFormOptions.completionSchema`. Which fields are required follows the labels: everything
 * not marked "(optional)".
 */

export const adressePflichtfelderSchema = schema<AdresseFormValue>(path => {
  validate(path.plz, validateRequired('inputErrors.blank.plz'));
  validate(path.ort, validateRequired('inputErrors.blank.city'));
  validate(path.strasse, validateRequired('inputErrors.blank.street'));
  validate(path.hausnummer, validateRequired('inputErrors.blank.houseNumberStaircase'));
});

export const privatpersonPflichtfelderSchema = schema<PrivatpersonKontaktdatenFormValue>(path => {
  validate(path.vorname, validateRequired('inputErrors.blank.firstName'));
  validate(path.nachname, validateRequired('inputErrors.blank.lastName'));
  validate(path.email, validateRequired('inputErrors.blank.email'));
});

export const unternehmenPflichtfelderSchema = schema<UnternehmenKontaktdatenFormValue>(path => {
  validate(path.name, validateRequired('inputErrors.blank.companyName'));
  validate(path.uid, validateRequired('inputErrors.blank.uidNr'));
  validate(path.email, validateRequired('inputErrors.blank.email'));
});

export const privatUnternehmenPflichtfelderSchema = schema<PrivatUnternehmenFormValue>(path => {
  validate(path.vertragspartnerTyp, validateRequired('inputErrors.blank.selection'));
  applyWhen(
    path.privatpersonKontaktdaten,
    ctx => ctx.valueOf(path.vertragspartnerTyp) === 'privat' && ctx.value() !== null,
    privatpersonPflichtfelderSchema,
  );
  applyWhen(
    path.unternehmenKontaktdaten,
    ctx => ctx.valueOf(path.vertragspartnerTyp) === 'unternehmen' && ctx.value() !== null,
    unternehmenPflichtfelderSchema,
  );
});

/** With the checkbox ticked, the Rechnungsempfänger's name and address must be filled in. */
export const abweichenderRechnungsempfaengerPflichtfelderSchema = schema<RechnungsempfaengerFormValue>(path => {
  applyWhen(
    path,
    ctx => ctx.value().hasAbweichendenRechnungsempfaenger,
    rechnungsempfaengerPath => {
      validate(rechnungsempfaengerPath.name, validateRequired('inputErrors.blank.companyName'));
      apply(rechnungsempfaengerPath.adresse, adressePflichtfelderSchema);
    },
  );
});
